import { isEntityLoaded, isEntityLoading } from '@/Models/Entity';
import { DobAlert, DobButton } from '@dedalusdiit/dob-components';
import classNames from 'classnames';
import { DeleteWorklistDialog } from '@/Pages/GenericWorklist/Components/DeleteWorklistDialog';
import GroupLevel from '@/Pages/GenericWorklist/Components/GroupLevel';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { CurrentWorklistDashboardHelper } from '../../Stores/CurrentWorklistDashboardHelper';
import { CustomWorklistHelper, ICustomWorklistHelper } from '../../Stores/CustomWorklistHelper';
import { type IWorklistStore } from '../../Stores/WorklistStore';
import WorklistToDashboardDialog from '../Dialogs/WorklistToDashboardDialog/WorklistToDashboardDialog';
import { type IWorklistToDashboardDialogViewStore } from '../Dialogs/WorklistToDashboardDialog/WorklistToDashboardDialogViewStore';
import WorklistFilters from './Filters/WorklistFilters';
import WorklistSideBarFooter from './Footer/WorklistSideBarFooter';
import SaveWorklistDialog from './SaveWorklistDialog';
import SavedFilterSelect from './SavedFilterSelect';
import Styles from './WorklistSidebar.module.scss';
import WorklistSidebarStore from './WorklistSidebarStore';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { WORKLIST_SETUP_DIALOG_COLUMN_TAB, WORKLIST_SETUP_DIALOG_FILTER_TAB } from '@/Pages/GenericWorklist/Stores/WorklistStoreDefaults';
import { WORKLIST_MINIMUM_CRITERIA_MESSAGE_KEY } from '@/Pages/GenericWorklist/Logic/WorklistFilterCriteriaLogic';

const {
    filterHeader: filterHeaderStyle,
    filtersContainer: filtersContainerStyle,
    sidebarContainer: sidebarContainerStyle,
    collapsed: collapsedStyle,
} = Styles;

export interface WorklistSidebarProps {
    worklistStore: IWorklistStore;
    customWorklistHelper: CustomWorklistHelper;
    currentWorklistDashboardHelper: CurrentWorklistDashboardHelper;
    worklistSidebarStore: WorklistSidebarStore;
    worklistToDashboardDialogViewStore: IWorklistToDashboardDialogViewStore;
}

const WorklistSidebar = (props: Readonly<WorklistSidebarProps>): React.ReactElement => {
    const { worklistStore, customWorklistHelper, currentWorklistDashboardHelper, worklistSidebarStore, worklistToDashboardDialogViewStore } = props;
    const {
        worklist: {
            Params: { Filter: activeFilter },
        },
    } = worklistStore;

    const [searchParams, setSearchParams] = useSearchParams();

    const customWorklistIdSearchParam = searchParams.get('id');
    const initialPageDataIsLoaded = isEntityLoaded(worklistStore.initialPageData);

    useEffect(() => {
        if (!initialPageDataIsLoaded) {
            return;
        } // Wait until all the correct data is loaded

        const worklistId = Number.parseInt(customWorklistIdSearchParam ?? '', 10) || undefined;

        if (worklistId != customWorklistHelper.currentlySelectedCustomWorklist?.ID) {
            worklistStore.setWorklistLoaded(false);
        }

        if (worklistStore.isWorklistLoaded) {
            return;
        }

        const customWorklist = customWorklistHelper.savedCustomWorklists.find((w) => w.ID === worklistId);

        if (customWorklist) {
            customWorklistHelper.activateCustomWorklistByID(customWorklist.ID);
        }
        else {
            customWorklistHelper.deselectCustomWorklist();
        }

        worklistStore.fetchResultForCurrentParams()
            .catch((e) => {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }
                console.error(`⛔ Fetching initial results failed`, e);
            });
    }, [initialPageDataIsLoaded, customWorklistIdSearchParam]);

    useEffect(() => {
        if (!initialPageDataIsLoaded) {
            return;
        }

        // This useEffect will trigger the one just above. This is necessary to split the updating of the search params from the actual activating of a worklist and fetching data
        // Otherwise we might trigger too many rerenders and get requests
        const worklistID = worklistStore.selectedCustomWorklistID;
        const customWorklist = customWorklistHelper.savedCustomWorklists.find((w) => w.ID === worklistID);

        if (customWorklist) {
            // Keep URL in sync so that the GenericWorklist effect deps detect
            // re-navigation to the same worklist via the nav menu.
            setSearchParams((prev) => {
                prev.delete('id');
                if (customWorklist?.Name) {
                    prev.set('id', customWorklist.ID.toString());
                }

                worklistStore.setPath(`${window.location.pathname}?id=${customWorklist.ID}`);
                return prev;
            }, { replace: true });
        }
        else {
            setSearchParams((prev) => {
                prev.delete('id');

                worklistStore.setPath(`${window.location.pathname}`);
                return prev;
            }, { replace: true });
        }
    }, [worklistStore.selectedCustomWorklistID]);

    useEffect(() => {
        worklistSidebarStore.setCurrentFilter(activeFilter);
    }, [worklistSidebarStore, activeFilter]);

    const { currentlySelectedCustomWorklist, savedCustomWorklists } = customWorklistHelper;
    const isLoading = isEntityLoading(worklistStore.results);
    const { isSidebarCollapsed } = worklistSidebarStore;

    const sidebarClassNames = classNames(sidebarContainerStyle, { [collapsedStyle]: isSidebarCollapsed });

    const onToggleSidebarCollapsed = (): void => {
        customWorklistHelper.toggleSidebarCollapsedState()
            .catch((e) => console.error(`⛔ Toggling sidebar collapsed failed`, e));
    };

    const onDeleteCurrentWorklist = (): void => {
        worklistSidebarStore.deleteActiveWorklist()
            .catch((e) => console.error(`⛔ Deleting the current worklist failed`, e));
    };

    const onSaveCurrentWorklist = async (name: string, groupLevel: GroupLevel, groupID?: number, description?: string): Promise<void> => {
        await customWorklistHelper.saveCurrentWorklistAsCustomWorklist(name, groupLevel, groupID, description);
    };

    const onUpdateCurrentWorklist = async (name: string, groupLevel: GroupLevel, groupID?: number, description?: string): Promise<void> => {
        return await customWorklistHelper.updateCurrentCustomWorklist(name, groupLevel, groupID, description);
    };

    return (
        <>
            <SaveWorklistDialog
                key="SaveNewWorklistDialog"
                isOpen={worklistSidebarStore.saveWorklistModalIsOpen}
                onClose={() => worklistSidebarStore.toggleSaveWorklistDialog(false)}
                onSave={onSaveCurrentWorklist}
                worklistStore={worklistStore}
            />
            <SaveWorklistDialog
                key="UpdateWorklistDialog"
                activeWorklist={customWorklistHelper.currentlySelectedCustomWorklist ?? undefined}
                isOpen={worklistSidebarStore.updateWorklistModalIsOpen}
                onClose={() => worklistSidebarStore.toggleUpdateWorklistDialog(false)}
                onSave={onUpdateCurrentWorklist}
                worklistStore={worklistStore}
            />

            <DeleteWorklistDialog
                key="DeleteWorklistDialog"
                worklistTitle={currentlySelectedCustomWorklist?.Name}
                isOpen={worklistSidebarStore.deleteModalIsOpen}
                onOk={onDeleteCurrentWorklist}
                onCancel={() => worklistSidebarStore.toggleDeleteModal(false)}
            />

            <WorklistToDashboardDialog
                key="WorklistToDashboardDialog"
                store={worklistToDashboardDialogViewStore}
                currentWorklistDashboardHelper={currentWorklistDashboardHelper}
            />

            <div className={sidebarClassNames}>
                <div className={filterHeaderStyle}>
                    <WorklistSettings
                        isCustomWorklistSelected={props.customWorklistHelper.isCustomWorklistSelected}
                        isSidebarCollapsed={isSidebarCollapsed}
                        openColumnsSetupDialog={props.worklistStore.openColumnsSetupDialog}
                        toggleSidebarCollapsed={onToggleSidebarCollapsed}
                    />
                    <CollapsibleSavedFilterSelect
                        isSidebarCollapsed={isSidebarCollapsed}
                        isDisabled={isLoading}
                        savedCustomWorklists={savedCustomWorklists}
                        currentCustomWorklist={currentlySelectedCustomWorklist}
                        selectSavedFilter={worklistStore.setSelectedCustomWorklistID}
                    />
                </div>
                <WorklistSidebarContent
                    isSidebarCollapsed={isSidebarCollapsed}
                    filtersContainerStyle={filtersContainerStyle}
                    store={worklistSidebarStore}
                    currentWorklistDashboardStore={currentWorklistDashboardHelper}
                    customWorklistsStore={customWorklistHelper}
                    isLoading={isLoading}
                    worklistStore={worklistStore}
                    worklistToDashboardDialogViewStore={worklistToDashboardDialogViewStore}
                />
            </div>
        </>
    );
};

interface WorklistSettingsProps {
    isCustomWorklistSelected: boolean;
    isSidebarCollapsed: boolean;
    openColumnsSetupDialog: ((tab: string) => void);
    toggleSidebarCollapsed: () => void;
}

function WorklistSettings({ isCustomWorklistSelected, isSidebarCollapsed, openColumnsSetupDialog, toggleSidebarCollapsed }: Readonly<WorklistSettingsProps>): React.ReactElement {
    const { t } = useTranslation();
    return (
        <div className={Styles.worklistSettings}>
            <WorklistSettingsButtons isSidebarCollapsed={isSidebarCollapsed} openColumnsSetupDialog={openColumnsSetupDialog} />
            <DobButton
                size="small"
                color="silent"
                icon={isSidebarCollapsed ? 'caret-right-fill' : 'caret-left-fill'}
                onClick={toggleSidebarCollapsed}
                title={isSidebarCollapsed ? t('ExpandSidebar') : t('CollapseSidebar')}
                disabled={!isCustomWorklistSelected}
            />
        </div>
    );
}

interface WorklistSettingsButtonsProps {
    isSidebarCollapsed: boolean;
    openColumnsSetupDialog: (tab: string) => void;
}

function WorklistSettingsButtons({ isSidebarCollapsed, openColumnsSetupDialog }: Readonly<WorklistSettingsButtonsProps>): React.ReactElement | null {
    const { t } = useTranslation();

    if (isSidebarCollapsed) {
        return null;
    }

    return (
        <>
            <DobButton
                size="small"
                color="silent"
                icon="funnel-fill"
                onClick={() => openColumnsSetupDialog(WORKLIST_SETUP_DIALOG_FILTER_TAB)}
                title={t('FilterSettings')}
            />
            <DobButton
                size="small"
                color="silent"
                icon="layout-three-columns"
                onClick={() => openColumnsSetupDialog(WORKLIST_SETUP_DIALOG_COLUMN_TAB)}
                title={t('ColumnSettings')}
            />
        </>
    );
}

interface CollapsibleSavedFilterSelectProps {
    isSidebarCollapsed: boolean;
    isDisabled: boolean;
    savedCustomWorklists: ICustomWorklistHelper['savedCustomWorklists'];
    currentCustomWorklist: ICustomWorklistHelper['currentlySelectedCustomWorklist'];
    selectSavedFilter: (id: number | null) => void;
}

function CollapsibleSavedFilterSelect({ isSidebarCollapsed, isDisabled, savedCustomWorklists, currentCustomWorklist, selectSavedFilter }: Readonly<CollapsibleSavedFilterSelectProps>): React.ReactElement | null {
    if (isSidebarCollapsed) {
        return null;
    }

    return (
        <SavedFilterSelect
            isDisabled={isDisabled}
            savedCustomWorklists={savedCustomWorklists}
            currentCustomWorklist={currentCustomWorklist}
            selectSavedFilter={selectSavedFilter}
        />
    );
}

interface WorklistSidebarContentProps {
    isSidebarCollapsed: boolean;
    filtersContainerStyle: string;
    store: WorklistSidebarStore;
    currentWorklistDashboardStore: CurrentWorklistDashboardHelper;
    customWorklistsStore: CustomWorklistHelper;
    isLoading: boolean;
    worklistStore: IWorklistStore;
    worklistToDashboardDialogViewStore: IWorklistToDashboardDialogViewStore;
}

const WorklistSidebarContent = observer(({ isSidebarCollapsed, filtersContainerStyle, store, currentWorklistDashboardStore, customWorklistsStore, isLoading, worklistStore, worklistToDashboardDialogViewStore }: Readonly<WorklistSidebarContentProps>): React.ReactElement | null => {
    const { t } = useTranslation();

    if (isSidebarCollapsed) {
        return null;
    }

    return (
        <>
            <div className={filtersContainerStyle}>
                <WorklistFilters sideBarStore={store} />
                { store.showMinimumCriteriaAlert && (
                    <DobAlert type="warning" className={Styles.filterCriteriaAlert}>
                        {t(WORKLIST_MINIMUM_CRITERIA_MESSAGE_KEY)}
                    </DobAlert>
                )}
            </div>
            <WorklistSideBarFooter
                currentWorklistDashboardStore={currentWorklistDashboardStore}
                customWorklistsStore={customWorklistsStore}
                isLoading={isLoading}
                worklistSidebarStore={store}
                worklistStore={worklistStore}
                worklistToDashboardDialogViewStore={worklistToDashboardDialogViewStore}
            />
        </>
    );
});

export default observer(WorklistSidebar);
